import axios from "axios";
import { get, isEmpty } from "lodash";
import { error, log, warn } from "../../utils/logger";
import { toastr } from "react-redux-toastr";
import {
  CAMPAIGN_ERROR,
  SET_CAMPAIGNS,
  SELECT_CAMPAIGN,
  SET_PUSH_OPTIONS,
  SET_EMAIL_OPTIONS,
  SET_CAMPAIGN_TEMPLATES,
  SET_CAMPAIGN_TAGS,
  SET_CAMPAIGN_FOLDERS,
  SELECT_FOLDER,
  SET_NESTED_CAMPAIGN_FOLDERS,
  REPORT_REFRESHED,
  UPDATED_CAMPAIGN,
  DELETE_CAMPAIGN,
  RESET_CAMPAIGNS,
  CAMPAIGN_LOADING,
  SET_CALLER_IDS,
  CALLER_ID_USAGE_DETAILS,
  SET_CAMPAIGN_TYPES,
  SET_CAMPAIGN_SUBTYPES,
  WAV_FILE_UPLOAD,
  WAV_FILE_ERROR,
  REMOVE_WAV_FILE,
  LOAD_SAVED_FILES,
  REMOVE_FILES,
} from "../constants";

import { formatError, campaignUIToServer } from "../../utils/normalize";
import { getDataset } from "./datasetActions";
import FileDownload from "js-file-download";

import { encryption } from "../../utils/data.crypt";

const { REACT_APP_CRYPTO_KEY } = process.env;

export const SESSION_STORAGE_TOKEN_NAME = "mbEonsNewCampaignTemplate";
export const ALLOW_DOWNLOAD_STATUSES = [-1, 1, 2, 3];

export const addError = (error) => (dispatch) => {
  dispatch({ type: CAMPAIGN_ERROR, payload: error });
};

// Pass-through functions
export const selectCampaign = (campaign) => (dispatch) => {
  dispatch({ type: SELECT_CAMPAIGN, payload: campaign || [] });
};

// ================================================================================================
// Campaign Config Options ========================================================================
// ================================================================================================

export const getEmailConfigOptions = () => (dispatch) => {
  log("Getting email options for campaign");

  return axios
    .get("campaign/emailOptions")
    .then((res) => {
      const email_options = get(res, "data.email_options");
      if (isEmpty(email_options)) {
        return warn("No email options...");
      }
      dispatch({ type: SET_EMAIL_OPTIONS, payload: email_options });

      return email_options;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error getting email options", error_message);
      dispatch(addError({ emailOptions: error_message }));

      toastr.error(
        "Sorry",
        "It seems [email] is not yet configured. Please contact your service agent"
      );
    });
};

export const getPushNotificationConfigOptions = () => (dispatch) => {
  log("Getting push options for campaign");

  return axios
    .get("campaign/pushNotificationOptions")
    .then((res) => {
      const push_options = get(res, "data.push_options");
      if (isEmpty(push_options)) {
        return warn("No push options...");
      }
      dispatch({ type: SET_PUSH_OPTIONS, payload: push_options });

      return push_options;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error getting push options", error_message);
      dispatch(addError({ pushOptions: error_message }));

      toastr.error(
        "Sorry",
        "It seems [push] is not yet configured. Please contact your service agent"
      );
    });
};

export const getCallerIdOptions = () => (dispatch) => {
  log("Getting caller ID options");

  return axios
    .get("campaign/callerIdOptions")
    .then((res) => {
      const caller_ids = get(res, "data.caller_ids");
      if (isEmpty(caller_ids)) {
        return warn("No caller IDs...");
      }
      dispatch({ type: SET_CALLER_IDS, payload: caller_ids });

      return caller_ids;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error getting caller ids", error_message);
      dispatch(addError({ callerIds: error_message }));

      toastr.error(
        "Sorry",
        "It seems [voice] is not yet configured. Please contact your service agent"
      );
    });
};

// Category = types | subTypes
export const getCampaignTypes = (category, type) => (dispatch) => {
  log(`Getting campaign ${category}`);

  let DISPATCH_TYPE;
  switch (category) {
    case "types":
      DISPATCH_TYPE = SET_CAMPAIGN_TYPES;
      break;
    case "subTypes":
      DISPATCH_TYPE = SET_CAMPAIGN_SUBTYPES;
      break;
    default:
      throw new Error(`Invalid category[${category}]`);
  }

  return axios
    .get(`campaign/getPreference/${category}`, { params: { type } })
    .then((res) => {
      const results = get(res, `data.${category}`);
      if (isEmpty(results)) {
        warn(`No ${category}...`);
      }

      dispatch({ type: DISPATCH_TYPE, payload: results });

      return results;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error getting caller ids", error_message);
      dispatch(addError({ callerIds: error_message }));

      toastr.error("Sorry", `Unable to find any [${category}]`);

      return [];
    });
};

// ================================================================================================
// Campaign Operations ============================================================================
// ================================================================================================

export const getCampaign = (campaign_id, get_dataset, approval_flow) => (dispatch) => {
  log(`Getting campaign[${campaign_id}]: get_dataset[${get_dataset}], approval[${approval_flow}]`);

  let query_string = "";
  if (approval_flow) {
    query_string += "?flow=a";
  }

  return axios
    .get(`campaign/${campaign_id}${query_string}`)
    .then((res) => {
      const campaign = get(res, "data.campaign");
      if (isEmpty(campaign)) {
        return error(`Campaign[${campaign_id}] not found`);
      }

      // Normalize campaign for front-end
      campaign.name = campaign.title_vch;
      campaign.folder = { value: campaign.folder_id_bint };
      campaign.mapped_variables = campaign.vars_vch ? JSON.parse(campaign.vars_vch) : {};
      campaign.permissions = campaign.permission_ch;
      let jsonTagsList = campaign.tags_list ? JSON.parse(campaign.tags_list) : [];
      if (jsonTagsList && Array.isArray(jsonTagsList)) {
        jsonTagsList = jsonTagsList
          .filter((item) => item)
          .map((item) => {
            return { label: item, value: item };
          });
      } else {
        jsonTagsList = [];
      }

      campaign.tags_list = jsonTagsList;

      // Calculated values
      if (campaign.type_vch) {
        campaign.type = { label: campaign.type_vch, value: campaign.type_vch };
        if (campaign.subType) {
          campaign.subType = { label: campaign.subType_vch, value: campaign.subType_vch };
        }
      }

      ["email", "sms", "voice", "push"].forEach((type) => {
        if (!campaign[`${type}_data_txt`]) {
          campaign[type] = { enabled: false };
        } else {
          const type_data = JSON.parse(campaign[`${type}_data_txt`]);
          campaign[type] = { enabled: true, ...type_data };
        }
      });

      dispatch(selectCampaign(campaign));
      //load files if files are saved
      if (campaign && campaign.voice_data_txt && campaign.voice_data_txt.files) {
        dispatch({ type: LOAD_SAVED_FILES, payload: campaign.voice_data_txt.files });
      }

      if (get_dataset) {
        dispatch(getDataset(campaign.dataset_id_bint, { campaign_id }));
      }

      return campaign;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error getting campaign", err);
      dispatch(addError(err.response.data));

      if (/not an approver/.test(error_message)) {
        toastr.error("Sorry", error_message);
      }
    });
};

// Get array of Campaigns
let campaignsCache, pendingCampaignsCall;
export const getCampaigns = (force_refresh) => (dispatch, getState) => {
  const { campaigns } = getState();

  if (pendingCampaignsCall) {
    error("Blocked campaign refresh: pending call in progress", pendingCampaignsCall);
    // current call in process
    return campaignsCache;
  }

  if (isEmpty(campaigns.list) || force_refresh) {
    log("Updating campaigns array");
    pendingCampaignsCall = true;

    campaignsCache = axios
      .get(`campaign`)
      .then((res) => {
        let campaigns = get(res, "data.campaigns");
        campaigns = (campaigns || []).map((campaign) => {
          let jsonTagsList = campaign.tags_list ? JSON.parse(campaign.tags_list) : [];
          if (jsonTagsList && Array.isArray(jsonTagsList)) {
            jsonTagsList = jsonTagsList
              .filter((item) => item)
              .map((item) => {
                return { label: item, value: item };
              });
          } else {
            jsonTagsList = [];
          }
          campaign.tags_list = jsonTagsList;
          return campaign;
        });
        if (isEmpty(campaigns)) return warn("No campaigns found");

        dispatch({ type: SET_CAMPAIGNS, payload: campaigns });

        return campaigns;
      })
      .catch((err) => {
        error(
          "Error getting campaigns",
          err && err.response && err.response.data ? err.response.data : err
        );
        dispatch(addError(err.response.data));
      })
      .finally(() => {
        pendingCampaignsCall = false;
      });
  } else {
    log("Using cached campaigns");
  }

  return campaignsCache;
};

export const sendSampleCampaign = (newCampaign, sampleRecords, seedList, files) => {
  log("Sending sample campaign");

  // Normalize UI Campaign Object for back-end
  const campaign = campaignUIToServer({ ...newCampaign });

  if (files && files.length) {
    campaign.voice.files = files.map((file) => ({
      name: file.name,
      original_name: file.original_name,
    }));
  }

  axios
    .post(`campaign/samples`, { campaign, sampleRecords, seedList })
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        const message = get(res, "data.message");
        toastr.success("Success", message);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      error("Error sending sample message", error_message || get(err, "response.data"));

      toastr.error(
        "Sorry",
        `There was an issue sending your sample campaign, please try again in a few minutes`
      );
    });
};

export const createCampaign = (campaign, files) => (dispatch) => {
  log(`Creating campaign[${campaign.name}]`);

  const campaignData = campaignUIToServer(campaign);
  if (files && files.length) {
    campaignData.voice.files = files.map((file) => ({
      name: file.name,
      original_name: file.original_name,
    }));
  }
  const payload = JSON.stringify(campaignData);
  const encryptedPayload = encryption(REACT_APP_CRYPTO_KEY, payload);

  return axios
    .put("/campaign", encryptedPayload)
    .then((res) => {
      const new_campaign = get(res, "data.campaign");
      if (isEmpty(new_campaign)) {
        throw formatError({ dataset: "Unknown error creating campaign" });
      }

      toastr.success("Success", `Created campaign ${campaign.name}`);
      dispatch(getCampaign(new_campaign.campaign_id_bint));
      dispatch(getCampaigns(true));
      dispatch({ type: WAV_FILE_UPLOAD, payload: [] });

      return new_campaign;
    })
    .catch((err) => {
      error("Error creating campaign message", err.response);
      toastr.error(
        "Sorry",
        `There was an issue creating your campaign, please try again in a few minutes`
      );

      const campaign_id_bint = get(err, "response.data.error.campaign_id");
      if (campaign_id_bint) {
        warn(`  [i] BUT, campaign was saved to database as campaign[${campaign_id_bint}]`);
        return { status: "error", campaign_id_bint };
      }
    });
};
export const getCallerIdUsageInfo = () => (dispatch) => {
  log(`fetching caller id info `);
  return axios
    .get(`/campaign/fetchCallerIds`)
    .then((res) => {
      const data = get(res, "data.result");
      /* if (isEmpty(data)) {
        throw formatError({ dataset: "Unknown error creating campaign" });
      } */
      //log("campaign info received after saving ", data);
      dispatch({ type: CALLER_ID_USAGE_DETAILS, payload: data });
      return data;
    })
    .catch((err) => {
      error("Error fetching caller id last used info", err.response);
      /* toastr.error(
        "Sorry",
        `There was an issue saving progress, please try again in a few minutes`
      ); */
    });
};

export const saveCampaignProgress = (campaign, files) => (dispatch) => {
  log(`Saving draft ${JSON.stringify(campaign)}]`);
  let route = `/campaign/saveProgress`;

  const campaignData = campaignUIToServer(campaign);
  if (files && files.length) {
    campaignData.voice.files = files.map((file) => ({
      name: file.name,
      original_name: file.original_name,
    }));
  }
  const payload = JSON.stringify(campaignData);
  const encryptedPayload = encryption(REACT_APP_CRYPTO_KEY, payload);
  return axios
    .put(route, encryptedPayload)
    .then((res) => {
      const new_campaign = get(res, "data.campaign");
      if (isEmpty(new_campaign)) {
        throw formatError({ dataset: "Unknown error creating campaign" });
      }

      log("campaign info received after saving ", new_campaign);
      toastr.success("Success", `Created campaign ${campaign.name}`);
      //dispatch({ type: WAV_FILE_UPLOAD, payload: [] });
      dispatch(getCampaign(new_campaign.campaign_id_bint));
      dispatch(getCampaigns(true));
      return new_campaign;
    })
    .catch((err) => {
      error("Error saving progress", err.response);
      toastr.error(
        "Sorry",
        `There was an issue saving progress, please try again in a few minutes`
      );

      const campaign_id_bint = get(err, "response.data.error.campaign_id");
      if (campaign_id_bint) {
        warn(`  [i] BUT, draft was saved to database as campaign[${campaign_id_bint}]`);
        return { status: "error", campaign_id_bint };
      }
    });
};

export const validatePin = (campaign_id, pin) => {
  log(`Validating PIN for campaign[${campaign_id}]`);

  return axios
    .post("/campaign/validatePin", { campaign_id, pin })
    .then((res) => {
      const status = get(res, "data.status");
      if (status !== "success") {
        throw formatError({ pin: "Invalid PIN" });
      }

      return { valid: get(res, "data.message" || "PIN Validated") };
    })
    .catch((err) => {
      const error_message =
        get(err, "response.data.error") || "Unknown Error, please try again in a few moments";
      warn("Error validating pin", error_message);
      return { error: error_message };
    });
};

export const approveCampaign = (campaign_id, pin, suppress_optouts) => (dispatch) => {
  log(`Approve campaign[${campaign_id}]`);

  return axios
    .post("/campaign/approve", { campaign_id, pin, suppress_optouts })
    .then((res) => {
      const status = get(res, "data.status");
      if (status !== "success") {
        throw formatError({ approve: "Unable to approve campaign" });
      }

      dispatch(getCampaigns(true));
      return { valid: get(res, "data.message" || "Campaign approved") };
    })
    .catch((err) => {
      const error_message =
        get(err, "response.data.error") || "Unknown Error, please try again in a few moments";
      warn("Error approving campaign", error_message);
      return { error: error_message };
    });
};

export const updateCampaign = (campaign_id, campaign) => (dispatch) => {
  log(`Updating campaign[${campaign_id}]`, campaign);

  dispatch({ type: UPDATED_CAMPAIGN });

  return axios
    .patch(`campaign/${campaign_id}`, { campaign })
    .then((res) => {
      log(`Update successful`, get(res, "data.message"));

      // Refresh UI
      dispatch(getCampaigns(true));
      dispatch(getCampaign(campaign_id));

      return { status: "ok" };
    })
    .catch((err) => {
      const error_message =
        get(err, "response.data.error") || "Unknown Error, please try again in a few moments";

      toastr.error(error_message);
      return { error: error_message };
    });
};

export const deleteCampaign = (campaign_id) => (dispatch) => {
  log(`[i] Deleting campaign[${campaign_id}]`);

  return axios
    .delete(`campaign/${campaign_id}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        log(`  [i] Deleted campaign[${campaign_id}]`);

        dispatch({ type: DELETE_CAMPAIGN });
        dispatch(getCampaigns(true));
        toastr.success("Success", `Deleted campaign: [${campaign_id}]`);
      }
      return { status };
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      log({ warn: `[i] Error deleting campaign[${campaign_id}]` });

      dispatch({ type: CAMPAIGN_ERROR, payload: { delete: "Error deleting campaign" } });

      return { error: error_message };
    });
};

// ================================================================================================
// Campaign Report Operations =====================================================================
// ================================================================================================

export const refreshCampaignReport = (campaign_id) => (dispatch) => {
  log(`Refreshing campaign[${campaign_id}] report`);

  dispatch({ type: CAMPAIGN_LOADING, payload: "report" });

  axios
    .get(`campaign/report/refresh/${campaign_id}`)
    .then((res) => {
      dispatch({ type: REPORT_REFRESHED });

      const message = get(res, "data.message");
      log("Report Refresh Response", message);
      toastr.success("Successfully refreshed report!");

      // Refresh UI
      dispatch(getCampaigns(true));
      dispatch(getCampaign(campaign_id));
    })
    .catch((err) => {
      const error_message =
        get(err, "response.data.error") || "Unknown Error, please try again in a few moments";
      dispatch({ type: REPORT_REFRESHED });
      if (typeof error_message === "string") {
        toastr.error(error_message || "");
      } else {
        toastr.error("Unknown Error, please try again in a few moments");
      }
      //return { error: error_message };
    });
};

export const downloadCampaign = (campaign_id, file_name = "campaign.csv") => {
  log("Downloading campaign", campaign_id, "as", file_name);

  axios
    .get(`/campaign/download/${campaign_id}`)
    .then((res) => FileDownload(res.data, file_name))
    .catch((err) => {
      const message = get(err, "err.response.data.error") || "Unauthorized";
      toastr.error(message || "Sorry, there was an error downloading your campaign");
    });
};

export const resendCampaignApprovals = (campaign_id) => {
  log(`Resending Approval Codes for Campaign ${campaign_id}`);

  axios
    .post(`/campaign/resendApproval/${campaign_id}`)
    .then((res) => toastr.success(res.data.message))
    .catch((err) => {
      const message = get(err, "err.response.data.error");
      toastr.error(message || "Sorry, there was an error resending the approval codes");
    });
};

// ================================================================================================
// Campaign Folder Operations =====================================================================
// ================================================================================================

let campaignTemplatesCache;
export const getCampaignTemplates = (force_refresh) => (dispatch) => {
  if (!campaignTemplatesCache || force_refresh) {
    log("Updating campaign templates");

    campaignTemplatesCache = axios
      .get(`campaign/templates`)
      .then((res) => {
        const templates = get(res, "data.templates");
        dispatch({ type: SET_CAMPAIGN_TEMPLATES, payload: templates });
        return "Campaign folder created";
      })
      .catch((err) => {
        error("Error getting campaign templates", err);
        dispatch(addError(err));

        const error_message = get(err, "err.response");
        throw error_message || "Unknown error creating campaign folder";
      });
  }

  return campaignTemplatesCache;
};

export const createCampaignFolder = (folder) => (dispatch) => {
  const { folderName, parent } = folder;
  log(`Creating campaign folder[${folderName}]${parent ? ` under parent[${parent}]` : ""}`);

  return axios
    .put(`campaign/folder`, folder)
    .then((res) => {
      const status = get(res, "data.status");
      if (status !== "success") return error("Error creating folder");

      // Will refresh: campaigns, folders, nestedFolders, nestedCampaignFolders
      dispatch(getNestedCampaignFolders(true));
      return "Campaign folder created";
    })
    .catch((err) => {
      error("Error creating campaign folder", err);
      dispatch(addError(err));

      const error_message = get(err, "err.response");
      throw error_message || "Unknown error creating campaign folder";
    });
};

export const selectCampaignFolder = (folder) => (dispatch) => {
  dispatch({ type: SELECT_FOLDER, payload: folder });
};

let campaignFoldersCache;
export const getCampaignFolders =
  (force_refresh, preselected_folder_id) => (dispatch, getState) => {
    const { campaigns } = getState();

    if (isEmpty(campaigns.folders) || force_refresh) {
      log("Updating campaign folders");

      campaignFoldersCache = axios
        .get(`campaign/folder`)
        .then((res) => {
          const folders = get(res, "data.folders");
          if (isEmpty(folders)) return error("No campaigns found");

          dispatch({ type: SET_CAMPAIGN_FOLDERS, payload: folders });

          if (preselected_folder_id !== undefined) {
            const selected = folders.find(
              (folder) => folder.folder_id_bint === Number(preselected_folder_id)
            );

            dispatch({ type: SELECT_FOLDER, payload: selected });
          }

          return folders;
        })
        .catch((err) => {
          error("Error getting campaign folders", err);
          dispatch(addError(err));
        });
    }

    return campaignFoldersCache;
  };

let campaignTagsCache;
export const getCampaignTags = () => (dispatch, getState) => {
  log("Updating campaign tags");
  dispatch({ type: CAMPAIGN_LOADING, payload: "campaign_tags" });
  campaignTagsCache = axios
    .get(`campaign/tagslist`)
    .then((res) => {
      let tags_list = get(res, "data.tags_list");
      if (isEmpty(tags_list)) tags_list = [];

      dispatch({ type: SET_CAMPAIGN_TAGS, payload: tags_list });

      return tags_list;
    })
    .catch((err) => {
      error("Error getting campaign tags", err);
      dispatch({ type: SET_CAMPAIGN_TAGS, payload: [] });
      dispatch(addError(err));
    });
  return campaignTagsCache;
};

export const getNestedCampaignFolders = (force_refresh) => (dispatch) => {
  log("Updating nested campaign folders");

  dispatch({ type: CAMPAIGN_LOADING, payload: "nested_campaign_folders" });

  // Ensure both campaigns and folders are available
  return Promise.all([
    dispatch(getCampaigns(force_refresh)), // Get array of campaigns
    dispatch(getCampaignFolders(force_refresh)), // Get array of folders
  ])
    .then(([campaigns, folders]) => {
      // Normalize campaign for the array => nested object process
      campaigns &&
        campaigns.map((campaign) => {
          if (campaign.parentid_bi === undefined) {
            campaign.parentid_bi = campaign.folder_id_bint;
            campaign.folder_id_bint = `campaign_${campaign.campaign_id_bint}`;
          }
          return campaign;
        });

      dispatch({
        type: SET_NESTED_CAMPAIGN_FOLDERS,
        payload: [...(folders || []), ...(campaigns || [])],
      });
    })
    .catch((err) => {
      error("Error getting campaign folders", err);
      dispatch(addError(err));
    });
};
export const moveCampaignsInFolders = (campaignInfo) => (dispatch) => {
  const { campaign, folder } = campaignInfo;
  log(`moving campaign ${campaign} to folder ${folder}`);

  return axios
    .put(`/campaign/moveCampaign`, campaignInfo)
    .then((res) => {
      const status = get(res, "data.status");
      if (status !== "success") return error("Error moving campaign");
      dispatch(getCampaigns(true));
      return "Campaign moved successfully";
    })
    .catch((err) => {
      error("Error moving campaign to folder", err);
      dispatch(addError(err));
      const error_message = get(err, "err.response");
      throw error_message || "Unknown error moving campaign to folder";
    });
};

// Upload temp files to use as dataset sources
export const uploadTempData = (files) => (dispatch) => {
  if (isEmpty(files)) {
    return error("No Files sent for upload");
  }

  log(`Uploading ${files.length} files`);

  const data = new FormData();
  files.forEach((file) => data.append("data", file));

  // '/files' is your node.js route that triggers our middleware
  return axios
    .post("/campaign/upload", data)
    .then((res) => {
      let temp_files = get(res, "data.files");
      if (isEmpty(temp_files)) {
        return dispatch({ type: CAMPAIGN_ERROR, payload: { files: "No files uploaded" } });
      }

      // Re-append original file name
      temp_files = temp_files.map((temp_file) => {
        const found = files.find((file) => file.name === temp_file.name);
        temp_file.original_name = found.original_name;
        return temp_file;
      });
      dispatch({ type: WAV_FILE_UPLOAD, payload: temp_files });

      return temp_files;
    })
    .catch((err) => {
      toastr.error("Oops", "There was an issue uploading your file(s)");
      error("Error uploading wav file", err.response ? err.response.data : err.message);
      dispatch({ type: WAV_FILE_ERROR, payload: { files: get(err, "response.data.error") } });
    });
};

export const removeTempFileData = (files, campaignId) => (dispatch) => {
  if (isEmpty(files)) return error("No Files to remove");

  return axios
    .delete(`campaign/upload`, { data: { files, campaignId } })
    .then((res) => {
      dispatch({ type: REMOVE_WAV_FILE, payload: get(res, "data.removed") });
      return res.data;
    })
    .catch((err) => {
      toastr.error("Oops", "There was an issue removing your file");
      error("Error removing temp file", err.response ? err.response.data : err.message);
      dispatch({ type: WAV_FILE_ERROR, payload: { files: get(err, "response.data.error") } });
    });
};

export const savedCampaignFilesLoad = (files) => (dispatch) => {
  if (isEmpty(files)) {
    return error("No Files sent for upload");
  }

  log(`loading ${files.length} files`);

  dispatch({ type: LOAD_SAVED_FILES, payload: files });
};

export const removeFiles = () => (dispatch) => {
  dispatch({ type: REMOVE_FILES, payload: [] });
};

//reset landing reducer state
export const resetState = () => ({ type: RESET_CAMPAIGNS });
